import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import SelectWithAdd from "@/components/SelectWithAdd";
import SearchableSelect from "@/components/SearchableSelect";
import DatePickerField from "@/components/DatePickerField";
import { toast } from "sonner";
import { Th, Td } from "@/pages/CustomersPage";
import { Search, RotateCcw, Download } from "lucide-react";
import { format, parseISO } from "date-fns";
import { safeFormatDate } from "@/lib/utils";

const EMPTY = {
  sim_service_provider_name: "",
  customer_id: "",
  customer_country: "",
  customer_city: "",
  mobile_number: "",
  device_model_name: "",
  installation_type_name: "",
  vehicle_number: "",
  invoice_type: "",
  installed_employee_name: "",
  sim_no: "",
  device_imei_number: "",
  installation_date_from: "",
  installation_date_to: "",
  next_invoice_date_from: "",
  next_invoice_date_to: "",
  sim_renewal_date_from: "",
  sim_renewal_date_to: "",
};

const INVOICE_TYPES = [
  { id: "Monthly", label: "Monthly" },
  { id: "Yearly", label: "Yearly" },
];

export default function SearchPage() {
  const [filters, setFilters] = useState(EMPTY);
  const [results, setResults] = useState([]);
  const [providers, setProviders] = useState([]);
  const [deviceModels, setDeviceModels] = useState([]);
  const [installTypes, setInstallTypes] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    api.get("/providers").then((r) => setProviders(r.data));
    api.get("/device-models").then((r) => setDeviceModels(r.data));
    api.get("/installation-types").then((r) => setInstallTypes(r.data));
    api.get("/employees").then((r) => setEmployees(r.data));
    api.get("/customers").then((r) => setCustomers(r.data));
    api.get("/geo/countries").then((r) => setCountries(r.data));
    runSearch(EMPTY);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!filters.customer_country) { setCities([]); return; }
    api.get(`/geo/cities?country_code=${filters.customer_country}`).then((r) => setCities(r.data));
  }, [filters.customer_country]);

  const update = (patch) => setFilters((f) => ({ ...f, ...patch }));

  const runSearch = async (vals) => {
    setSearching(true);
    try {
      const params = Object.fromEntries(
        Object.entries(vals).filter(([_, v]) => v !== "" && v != null)
      );
      const { data } = await api.get("/vehicles/search", { params });
      setResults(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setSearching(false);
    }
  };

  const submit = (e) => {
    e.preventDefault();
    runSearch(filters);
  };

  const reset = () => {
    setFilters(EMPTY);
    runSearch(EMPTY);
  };

  const downloadExcel = async () => {
    try {
      const params = Object.fromEntries(
        Object.entries(filters).filter(([_, v]) => v !== "" && v != null)
      );
      const res = await api.get("/vehicles/export", {
        params,
        responseType: "blob",
      });
      const totalRows =
        res.headers["x-total-rows"] || results.length;
      const link = document.createElement("a");
      link.href = URL.createObjectURL(res.data);
      const stamp = new Date().toISOString().slice(0, 19).replace(/[T:]/g, "_");
      link.download = `vehicles_export_${stamp}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(link.href);
      toast.success(`Downloaded ${totalRows} record(s)`);
    } catch (e) {
      const detail = e?.response?.data;
      let msg = "Export failed";
      if (detail instanceof Blob) {
        try {
          msg = JSON.parse(await detail.text())?.detail || msg;
        } catch (parseErr) {
          console.warn("Could not parse export error response:", parseErr);
        }
      } else if (typeof detail === "string") {
        msg = detail;
      }
      toast.error(msg);
    }
  };

  return (
    <div data-testid="search-page">
      <PageHeader
        overline="Operations"
        title="Search Details"
        description="Multi-field search across all installations. Combine any filters; leave blank to skip. Download current results as an Excel file."
        actions={
          <Button
            type="button"
            onClick={downloadExcel}
            data-testid="search-export-excel"
            className="rounded-none vsm-primary-bg hover:opacity-90"
          >
            <Download className="w-4 h-4 mr-2" />
            Download Excel ({results.length})
          </Button>
        }
      />

      <div className="px-4 sm:px-6 md:px-10 py-5 sm:py-8 space-y-6">
        <form onSubmit={submit} className="vsm-fieldset space-y-4" data-testid="search-form">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <Field label="SIM Service Provider">
              <SelectWithAdd
                testId="search-provider"
                value={filters.sim_service_provider_name}
                onChange={(v) => update({ sim_service_provider_name: v })}
                options={providers.map((p) => ({ id: p.name, label: p.name }))}
                placeholder="Any"
              />
            </Field>
            <Field label="Customer Name">
              <SearchableSelect
                testId="search-customer"
                value={filters.customer_id}
                onChange={(v) => update({ customer_id: v })}
                options={customers.map((c) => ({ id: c.id, label: c.name }))}
                placeholder="Any"
                emptyMessage="No customers"
              />
            </Field>
            <Field label="Country">
              <SearchableSelect
                testId="search-country"
                value={filters.customer_country}
                onChange={(v) => update({ customer_country: v, customer_city: "" })}
                options={countries.map((c) => ({ id: c.code, label: c.name }))}
                placeholder="Any"
              />
            </Field>
            <Field label="City">
              <SearchableSelect
                testId="search-city"
                value={filters.customer_city}
                onChange={(v) => update({ customer_city: v })}
                options={cities.map((c) => ({ id: c.name, label: c.name }))}
                placeholder={filters.customer_country ? "Any" : "Pick country first"}
                disabled={!filters.customer_country}
              />
            </Field>
            <Field label="SIM Number">
              <Input data-testid="search-mobile" className="rounded-none" value={filters.mobile_number} onChange={(e) => update({ mobile_number: e.target.value })} />
            </Field>
            <Field label="Vehicle Number">
              <Input data-testid="search-vehicle" className="rounded-none" value={filters.vehicle_number} onChange={(e) => update({ vehicle_number: e.target.value })} />
            </Field>
            <Field label="Device Model">
              <SelectWithAdd
                testId="search-device-model"
                value={filters.device_model_name}
                onChange={(v) => update({ device_model_name: v })}
                options={deviceModels.map((d) => ({ id: `${d.manufacturer} ${d.model}`, label: `${d.manufacturer} ${d.model}` }))}
                placeholder="Any"
              />
            </Field>
            <Field label="Installation Type">
              <SelectWithAdd
                testId="search-install-type"
                value={filters.installation_type_name}
                onChange={(v) => update({ installation_type_name: v })}
                options={installTypes.map((t) => ({ id: t.name, label: t.name }))}
                placeholder="Any"
              />
            </Field>
            <Field label="Invoice Type">
              <SelectWithAdd
                testId="search-invoice-type"
                value={filters.invoice_type}
                onChange={(v) => update({ invoice_type: v })}
                options={INVOICE_TYPES}
                placeholder="Any"
              />
            </Field>
            <Field label="Installed Employee">
              <SelectWithAdd
                testId="search-employee"
                value={filters.installed_employee_name}
                onChange={(v) => update({ installed_employee_name: v })}
                options={employees.map((e) => ({ id: e.name, label: e.name }))}
                placeholder="Any"
              />
            </Field>
            <Field label="SIM No">
              <Input data-testid="search-sim-no" className="rounded-none" value={filters.sim_no} onChange={(e) => update({ sim_no: e.target.value })} />
            </Field>
            <Field label="Device IMEI">
              <Input data-testid="search-imei" className="rounded-none" value={filters.device_imei_number} onChange={(e) => update({ device_imei_number: e.target.value })} />
            </Field>
          </div>

          <div className="border-t vsm-border pt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
            <DateRange label="Installation Date" testId="installation-date"
              from={filters.installation_date_from} to={filters.installation_date_to}
              onFrom={(v) => update({ installation_date_from: v })}
              onTo={(v) => update({ installation_date_to: v })}
            />
            <DateRange label="Next Invoice Date" testId="next-invoice-date"
              from={filters.next_invoice_date_from} to={filters.next_invoice_date_to}
              onFrom={(v) => update({ next_invoice_date_from: v })}
              onTo={(v) => update({ next_invoice_date_to: v })}
            />
            <DateRange label="SIM Renewal Date" testId="sim-renewal-date"
              from={filters.sim_renewal_date_from} to={filters.sim_renewal_date_to}
              onFrom={(v) => update({ sim_renewal_date_from: v })}
              onTo={(v) => update({ sim_renewal_date_to: v })}
            />
          </div>

          <div className="flex gap-2 justify-end pt-2">
            <Button type="button" variant="outline" className="rounded-none" onClick={reset} data-testid="search-reset">
              <RotateCcw className="w-4 h-4 mr-2" /> Reset
            </Button>
            <Button type="submit" disabled={searching} className="rounded-none vsm-primary-bg hover:opacity-90" data-testid="search-submit">
              <Search className="w-4 h-4 mr-2" /> {searching ? "Searching…" : "Search"}
            </Button>
          </div>
        </form>

        <div className="flex items-center">
          <div className="vsm-overline">Results</div>
          <div className="vsm-overline ml-auto">{results.length} match(es)</div>
        </div>

        <div className="border vsm-border vsm-surface overflow-x-auto vsm-table-scroll" data-testid="search-results-table">
          <table className="w-full text-sm whitespace-nowrap">
            <thead>
              <tr className="border-b vsm-border text-left">
                <Th>Vehicle #</Th>
                <Th>Country</Th>
                <Th>City</Th>
                <Th>Customer</Th>
                <Th>SIM Provider · Account</Th>
                <Th>SIM Number</Th>
                <Th>Device</Th>
                <Th>Install Types</Th>
                <Th>Installed</Th>
                <Th>Renewal</Th>
                <Th>Invoice</Th>
              </tr>
            </thead>
            <tbody>
              {results.length === 0 && (
                <tr>
                  <td colSpan={11} className="px-4 py-10 vsm-muted text-center">No matches.</td>
                </tr>
              )}
              {results.map((r) => (
                <tr key={r.id} className="border-b vsm-border hover:bg-zinc-50 dark:hover:bg-zinc-800/40">
                  <Td className="font-semibold">{r.vehicle_number}</Td>
                  <Td>{r.customer_country || "—"}</Td>
                  <Td>{r.customer_city || "—"}</Td>
                  <Td>{r.customer_name}</Td>
                  <Td>
                    {r.sim_service_provider_name}
                    {r.sim_service_provider_account_number && (
                      <span className="vsm-muted ml-1">· {r.sim_service_provider_account_number}</span>
                    )}
                  </Td>
                  <Td>{r.mobile_number}</Td>
                  <Td>{r.device_model_name}</Td>
                  <Td className="max-w-xs truncate">{(r.installation_type_names || []).join(", ") || "—"}</Td>
                  <Td>{safeFormatDate(r.installation_date)}</Td>
                  <Td>{safeFormatDate(r.sim_next_renewal_date)}</Td>
                  <Td>{r.invoice_type}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="vsm-overline">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function DateRange({ label, from, to, onFrom, onTo, testId }) {
  return (
    <div>
      <Label className="vsm-overline">{label}</Label>
      <div className="mt-2 grid grid-cols-2 gap-2">
        <DatePickerField testId={`${testId}-from`} value={from} onChange={onFrom} placeholder="From" />
        <DatePickerField testId={`${testId}-to`} value={to} onChange={onTo} placeholder="To" />
      </div>
    </div>
  );
}
